import { ViewerShell } from '../shared/shell.js';
import { VIEWER_TOKENS } from '../shared/tokens.js';
import { createLogo, logoSizeDeclarations, shouldShowLogo } from './logo.js';
import type { Unmount } from './page-lifecycle.js';

/**
 * 盤面のリンク(`pv_`)のページ(V02)。
 *
 * **盤面の表示はシェル(`ViewerShell`)に任せる。** ここが持つのは外枠 ——
 * **ロゴの帯だけ**(#318 / [ADR-0053](../../../../docs/adr/0053-share-link-logo.md))。
 *
 * - **ロゴを出すリンクのときだけ**、画面の上に帯を出し、右端にロゴを置く(右上)。盤面は帯の下の残りの真ん中
 * - **出さないときは何もしない** —— ページの DOM とスタイルは帯ができる前のまま(決定 7)
 * - 出すかどうかは公開 API の `showLogo`。シェルがテンプレートを取れたとき(`onTemplate`)に決める。
 *   **取得に失敗したとき(期限切れ・失効を含む)は帯を出さない**(同意書の A4)。取れた後の背景の待ち
 *   (「読み込み中…」)の間はもう出ている。取れた後の再試行で 410 になったときは帯が残る(まれなので外さない)
 */
export function mountBoard(container: HTMLElement, token: string): Unmount {
  let band: LogoBand | null = null;

  const shell: ViewerShell = new ViewerShell(container, {
    token,
    // 同一オリジンで配信する(ビューアと公開 API は CloudFront の後ろ)。
    apiBase: globalThis.location.origin,
    fit: 'contain',
    /*
     * **画面に収める**(#212)。V02 は盤面だけを見せる画面なので、
     * **スクロールしないと全体が見えない状態を作らない。**
     *
     * V01(埋め込み)には渡さない —— 埋め込み先のページには他の要素があり、
     * **こちらが画面の高さを占有してはいけない。**
     */
    fitViewport: true,
    // **再試行でも呼ばれる**ので、2 回目以降は何もしない。
    onTemplate: (template) => {
      if (band !== null || !shouldShowLogo(template)) return;
      band = showLogoBand(container, (px) => {
        shell.setViewportOffset(px);
      });
    },
  });
  void shell.start();

  return () => {
    band?.remove();
    band = null;
    shell.destroy();
  };
}

interface LogoBand {
  /** 帯を外し、ページの並びを帯ができる前に戻す(bfcache から戻ると作り直す —— #306)。 */
  remove(): void;
}

/**
 * 盤面の上にロゴの帯を出す。
 *
 * **ページの並びを縦に切り替える**(帯が上・盤面は残りの真ん中 —— 共有フォルダのページの見出しと
 * 盤面の領域と同じ組み方)。**帯の高さは測り続けて、盤面の高さの上限から差し引く**
 * (`setViewportOffset` —— #267 と同じ仕組み)。
 */
function showLogoBand(container: HTMLElement, setOffset: (px: number) => void): LogoBand {
  const { body } = document;
  const bodyStyle = body.getAttribute('style');
  const containerStyle = container.getAttribute('style');

  body.style.flexDirection = 'column';
  body.style.alignItems = 'stretch';
  container.style.flex = '1';
  container.style.display = 'flex';
  container.style.alignItems = 'center';
  container.style.justifyContent = 'center';
  container.style.minHeight = '0';

  // **余白と線は共有フォルダの見出しとそろえる**(決定 6)。
  const header = document.createElement('header');
  header.setAttribute(
    'style',
    [
      'flex: none;',
      'display: flex;',
      'align-items: center;',
      // **ロゴは右端**(右上 —— 牧野さんの指定 / 2026-10-09)。
      'justify-content: flex-end;',
      'padding: 12px 16px;',
      `border-bottom: 1px solid ${VIEWER_TOKENS['border-default'] ?? 'transparent'};`,
    ].join(' '),
  );
  const logo = createLogo('signa-logo');
  logo.setAttribute('style', logoSizeDeclarations());
  header.append(logo);
  container.before(header);

  const measure = (): void => {
    setOffset(Math.ceil(header.getBoundingClientRect().height));
  };
  // **描画の前に 1 回差し引く**(盤面が一度大きく描かれてから縮むのを避ける)。以後は変わるたびに。
  measure();
  const observer = new ResizeObserver(measure);
  observer.observe(header);

  return {
    remove: () => {
      observer.disconnect();
      header.remove();
      restoreStyle(body, bodyStyle);
      restoreStyle(container, containerStyle);
    },
  };
}

function restoreStyle(element: HTMLElement, style: string | null): void {
  if (style === null) element.removeAttribute('style');
  else element.setAttribute('style', style);
}
